import { useEffect, useState, type RefObject } from "react";
import { Decimal, type DecimalString } from "@/lib/decimal";

/** Chart inputs accept API decimal strings; geometry is float maths on display values only. */
export type ChartNumber = number | DecimalString | null | undefined;

export function chartNumber(value: ChartNumber): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  return Decimal.tryFrom(value)?.toNumber() ?? null;
}

/** Maps a domain onto a pixel range. */
export function linearScale([d0, d1]: [number, number], [r0, r1]: [number, number]) {
  const span = d1 - d0 || 1;
  return (value: number) => r0 + ((value - d0) / span) * (r1 - r0);
}

/** About `count` round tick values covering [min, max] (1, 2, 2.5 or 5 × 10^n steps). */
export function niceTicks(min: number, max: number, count = 4): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
  if (min === max) return [min];
  const rawStep = (max - min) / Math.max(count, 1);
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const step = ([1, 2, 2.5, 5, 10].find((factor) => factor * magnitude >= rawStep) ?? 10) * magnitude;
  const start = Math.ceil(min / step) * step;
  const ticks: number[] = [];
  for (let tick = start; tick <= max + step * 1e-9; tick += step) ticks.push(Number(tick.toPrecision(12)));
  return ticks;
}

/** Domain padded to nice ticks; zero is included when all values are positive (bars, masses). */
export function niceDomain(values: number[], { includeZero = true } = {}): [number, number] {
  if (values.length === 0) return [0, 1];
  let min = Math.min(...values);
  let max = Math.max(...values);
  if (includeZero) {
    min = Math.min(min, 0);
    max = Math.max(max, 0);
  }
  if (min === max) return min === 0 ? [0, 1] : [min - 1, max + 1];
  const ticks = niceTicks(min, max, 4);
  const step = ticks.length > 1 ? (ticks[1] ?? 0) - (ticks[0] ?? 0) : max - min;
  return [Math.floor(min / step) * step, Math.ceil(max / step) * step];
}

/** Log axes step through 1, 2 and 5 times each power of ten. */
const LOG_FACTORS = [1, 2, 5];

function round12(value: number): number {
  return Number(value.toPrecision(12));
}

/** Maps a positive domain onto a pixel range logarithmically. */
export function logScale([d0, d1]: [number, number], [r0, r1]: [number, number]) {
  const start = Math.log10(d0);
  const span = Math.log10(d1) - start || 1;
  return (value: number) => r0 + ((Math.log10(value) - start) / span) * (r1 - r0);
}

/** From the 1-2-5 value at or below the smallest positive value to the one at or above the largest. */
export function logDomain(values: number[]): [number, number] {
  const positive = values.filter((value) => value > 0);
  if (positive.length === 0) return [1, 10];
  const min = Math.min(...positive);
  const max = Math.max(...positive);
  const low = 10 ** Math.floor(Math.log10(min));
  const from = [...LOG_FACTORS].reverse().find((factor) => factor * low <= min * (1 + 1e-9)) ?? 1;
  const high = 10 ** Math.floor(Math.log10(max));
  const to = [...LOG_FACTORS, 10].find((factor) => factor * high >= max * (1 - 1e-9)) ?? 10;
  const domain: [number, number] = [round12(from * low), round12(to * high)];
  return domain[0] === domain[1] ? [domain[0], round12(domain[1] * 2)] : domain;
}

/** The 1-2-5 values in [min, max]; past `maxCount` of them, only the powers of ten (when there are two). */
export function logTicks(min: number, max: number, maxCount = 7): number[] {
  if (!(min > 0) || !(max > min)) return min > 0 ? [min] : [];
  const ticks: number[] = [];
  for (let exponent = Math.floor(Math.log10(min)); exponent <= Math.ceil(Math.log10(max)); exponent += 1) {
    for (const factor of LOG_FACTORS) {
      const tick = round12(factor * 10 ** exponent);
      if (tick >= min * (1 - 1e-9) && tick <= max * (1 + 1e-9)) ticks.push(tick);
    }
  }
  if (ticks.length <= maxCount) return ticks;
  const powers = ticks.filter((tick) => Math.abs(Math.log10(tick) - Math.round(Math.log10(tick))) < 1e-9);
  return powers.length >= 2 ? powers : ticks;
}

/** Rendered width of an element (ResizeObserver), so SVG text stays at its real size. */
export function useElementWidth(ref: RefObject<HTMLElement | null>, fallback = 640): number {
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width;
      if (measured && measured > 0) setWidth(Math.round(measured));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}
